import { centsToFormattedDollars } from '@fruitful/fruitils';
import { type RuleSetDisplay, type RuleType } from '@fruitful/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import Animated, {
    Easing,
    interpolate,
    interpolateColor,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withSequence,
    withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    FruitfulLogoIcon,
    Icon,
    View,
    YStack,
} from 'smoothie';

import {
    type OnboardingMoneyMapBoxLayout,
    type OnboardingMoneyMapHighlightedBox,
    type OnboardingMoneyMapSystemBoxPositions,
} from '../../types/OnboardingMoneyMapTypes';
import { OnboardingMoneyMapPreviewAnimatedBox } from './OnboardingMoneyMapPreviewAnimatedBox';
import {
    GoalsSystemContent,
    IncomeSystemContent,
    MonthlyExpensesSystemContent,
} from './OnboardingMoneyMapPreviewSystem';
import {
    FlyingGoalIcon,
    TimelineView,
} from './OnboardingMoneyMapPreviewTimeline';
import {
    BAR_GAP,
    BAR_RADIUS,
    BILLS_COLOR,
    BILLS_SYSTEM_COLOR,
    computeTimelineIconTargets,
    CONNECTOR_LINE_HEIGHT,
    CURVE_TO_BAR_GAP,
    DEFAULT_SYSTEM_HEIGHT,
    type GoalIconPosition,
    GOALS_COLOR,
    GOALS_SYSTEM_COLOR,
    INCOME_BG,
    INCOME_SYSTEM_BG,
    INCOME_TEXT,
    LOGO_BOX_SIZE,
    LOGO_GAP,
    MAX_BAR_HEIGHT,
    MIN_BAR_HEIGHT,
    PATHS_HEIGHT,
    PILL_HEIGHT,
    PILL_TO_CURVES_GAP,
    SPEND_COLOR,
    SPEND_SYSTEM_COLOR,
    STEP_DURATION,
    STROKE_WIDTH,
    SYSTEM_GAP,
    toHex,
} from './onboardingMoneyMapUtils';

export type { GoalIconPosition };

const CURVE_DASH_LENGTH = 2000;

const DrawablePath = ({
    d,
    stroke,
    strokeWidth,
    drawn,
    duration,
}: {
    d: string;
    stroke: string;
    strokeWidth: number;
    drawn: boolean;
    duration: number;
}) => (
    <Path
        d={d}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
        strokeDasharray={`${CURVE_DASH_LENGTH}`}
        strokeDashoffset={drawn ? 0 : CURVE_DASH_LENGTH}
        style={{
            // @ts-expect-error CSS transition property for web SVG
            transition: drawn
                ? `stroke-dashoffset ${duration}ms ease-out`
                : 'none',
        }}
    />
);

interface MoneyMapPreviewBaseProps {
    showToggle?: boolean;
    ruleSetDisplays?: RuleSetDisplay[] | null;
    /** When provided, bars stretch to fill this height in split mode. */
    availableHeight?: number;
    /** Reports the Y positions of each system-view box relative to the component top. */
    onSystemBoxPositions?: (
        positions: OnboardingMoneyMapSystemBoxPositions,
    ) => void;
    /** When set, dims all boxes except the highlighted one to 50% opacity. */
    highlightedBox?: OnboardingMoneyMapHighlightedBox;
    /** When provided, the component fills this height and centers split-mode bars vertically. */
    containerHeight?: number;
    /** Called as the user drags the spend/goals slider (timeline mode). */
    onSpendSlideMove?: (spendCents: number) => void;
    /** Called when the user releases the spend/goals slider (timeline mode). */
    onSpendSlideEnd?: (spendCents: number) => void;
    /** When true, timeline mode renders only the graph (no slider or goals list). */
    graphOnly?: boolean;
    /** Reports the Y position and height of the timeline white card relative to the component top. */
    onTimelineContentLayout?: (layout: { y: number; height: number }) => void;
    /** Reports the computed system goal icon positions for persistence by the parent. */
    onSystemGoalIcons?: (positions: GoalIconPosition[]) => void;
    /** Saved system goal icon positions to use as fallback during timeline→system transition. */
    savedSystemGoalIcons?: GoalIconPosition[];
    /** When true, renders in the target mode immediately with no entry or loop animations. */
    disableAnimations?: boolean;
    /** Override the default MAX_BAR_HEIGHT for split mode bars. */
    maxBarHeight?: number;
    /** Initial value for drawLines in timeline mode (default: true). */
    drawLines?: boolean;
    /** Initial value for hideIcons in timeline mode (default: false). */
    hideIcons?: boolean;
    /** When true, icons scale in from 0 when they first appear. */
    popInIcons?: boolean;
}

interface MoneyMapPreviewSplitSystemProps extends MoneyMapPreviewBaseProps {
    mode?: 'split' | 'system';
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
}

interface MoneyMapPreviewTimelineProps extends MoneyMapPreviewBaseProps {
    mode: 'timeline';
    incomeTotalCents?: number;
    billsTotalCents?: number;
    spendTotalCents?: number;
    goalsTotalCents?: number;
}

type MoneyMapPreviewProps =
    | MoneyMapPreviewSplitSystemProps
    | MoneyMapPreviewTimelineProps;

const SegmentDot = ({
    seg,
    segTop,
    lineDotTravel,
    dotSize,
    labelCenterX,
    loopProgress,
    progressXW,
    incomeHex,
    goalsHex,
    lineDotColorBreak,
    billsHoldDuration,
    billsLabelFrac,
    targetGroup,
    goalForkThresholds,
}: {
    seg: { top: number; height: number };
    segTop: number;
    lineDotTravel: number;
    dotSize: number;
    labelCenterX: number;
    loopProgress: Animated.SharedValue<number>;
    progressXW: Animated.SharedValue<number>;
    incomeHex: string;
    goalsHex: string;
    lineDotColorBreak: number;
    billsHoldDuration: number;
    billsLabelFrac: number;
    targetGroup: Animated.SharedValue<number>;
    goalForkThresholds: number[];
}) => {
    const segOffset = seg.top - segTop + dotSize;

    const dotStyle = useAnimatedStyle(() => {
        const t = Math.max(0, loopProgress.value - 1);
        let dotT = t;
        let dotOpacity = progressXW.value < 1 ? 0 : 1;
        if (
            t > lineDotColorBreak &&
            t <= lineDotColorBreak + billsHoldDuration
        ) {
            dotT = lineDotColorBreak;
            dotOpacity = 0;
        } else if (t > lineDotColorBreak + billsHoldDuration) {
            dotT = t - billsHoldDuration + billsLabelFrac;
        }
        const currentGroup = targetGroup.value;
        if (
            currentGroup < goalForkThresholds.length &&
            dotT >=
                (goalForkThresholds[currentGroup] ?? 1) -
                    billsHoldDuration +
                    billsLabelFrac
        ) {
            dotOpacity = 0;
        }
        const globalY = dotT * Math.max(0, lineDotTravel);
        const localY = globalY - segOffset;
        return {
            opacity: dotOpacity,
            transform: [{ translateY: localY }],
            backgroundColor: interpolateColor(
                dotT,
                [0, lineDotColorBreak, lineDotColorBreak + 0.01, 1],
                [incomeHex, incomeHex, goalsHex, goalsHex],
            ),
        };
    });

    return (
        <View
            style={{
                position: 'absolute',
                left: labelCenterX - 2,
                top: seg.top,
                width: 4,
                height: seg.height,
                overflow: 'hidden',
                zIndex: 6,
            }}
            pointerEvents="none"
        >
            <Animated.View
                style={[
                    {
                        width: 4,
                        height: dotSize,
                        borderRadius: 2,
                    },
                    dotStyle,
                ]}
            />
        </View>
    );
};

export const OnboardingMoneyMapPreview = ({
    incomeTotalCents = 0,
    billsTotalCents = 0,
    spendTotalCents = 0,
    goalsTotalCents = 0,
    mode = 'split',
    ruleSetDisplays,
    availableHeight,
    onSystemBoxPositions,
    highlightedBox,
    containerHeight: containerHeightProp,
    onSpendSlideMove,
    onSpendSlideEnd,
    graphOnly,
    onTimelineContentLayout,
    onSystemGoalIcons,
    savedSystemGoalIcons,
    disableAnimations = false,
    maxBarHeight: maxBarHeightProp,
    drawLines: drawLinesProp,
    hideIcons: hideIconsProp,
    popInIcons = false,
}: MoneyMapPreviewProps) => {
    const resolvedMaxBarHeight = maxBarHeightProp ?? MAX_BAR_HEIGHT;
    const [contentWidth, setContentWidth] = useState(0);
    // Measured content heights for system state: [income, bills, spend, goals]
    const [systemHeights, setSystemHeights] = useState<Array<number | null>>([
        null,
        null,
        null,
        null,
    ]);

    const isAnimatingRef = useRef(false);
    const pendingHeightsRef = useRef<Map<number, number>>(new Map());
    const heightRafRef = useRef<number | null>(null);

    const flushPendingHeights = useCallback(() => {
        const pending = pendingHeightsRef.current;
        if (pending.size === 0) return;
        setSystemHeights((prev) => {
            let changed = false;
            const next = [...prev];
            pending.forEach((h, i) => {
                if (next[i] !== h) {
                    next[i] = h;
                    changed = true;
                }
            });
            pending.clear();
            return changed ? next : prev;
        });
    }, []);

    const updateSystemHeight = useCallback(
        (index: number, height: number) => {
            pendingHeightsRef.current.set(index, height);
            if (isAnimatingRef.current) return;
            if (heightRafRef.current != null) return;
            heightRafRef.current = requestAnimationFrame(() => {
                heightRafRef.current = null;
                flushPendingHeights();
            });
        },
        [flushPendingHeights],
    );

    // Track label box widths to make them all match the widest one
    const [labelWidths, setLabelWidths] = useState<number[]>([0, 0, 0]);
    const allLabelsMeasured = labelWidths.every((w) => w > 0);
    const maxLabelWidth = allLabelsMeasured ? Math.max(...labelWidths) : 0;
    const [braceHeight, setBraceHeight] = useState(0);

    const pendingWidthsRef = useRef<Map<number, number>>(new Map());
    const widthRafRef = useRef<number | null>(null);

    const flushPendingWidths = useCallback(() => {
        const pending = pendingWidthsRef.current;
        if (pending.size === 0) return;
        setLabelWidths((prev) => {
            let changed = false;
            const next = [...prev];
            pending.forEach((w, i) => {
                if (next[i] !== w) {
                    next[i] = w;
                    changed = true;
                }
            });
            pending.clear();
            return changed ? next : prev;
        });
    }, []);

    const updateLabelWidth = useCallback(
        (index: number, width: number) => {
            pendingWidthsRef.current.set(index, width);
            if (isAnimatingRef.current) return;
            if (widthRafRef.current != null) return;
            widthRafRef.current = requestAnimationFrame(() => {
                widthRafRef.current = null;
                flushPendingWidths();
            });
        },
        [flushPendingWidths],
    );

    useEffect(() => {
        return () => {
            if (heightRafRef.current != null)
                cancelAnimationFrame(heightRafRef.current);
            if (widthRafRef.current != null)
                cancelAnimationFrame(widthRafRef.current);
        };
    }, []);

    const onIncomeContentHeight = useCallback(
        (h: number) => {
            updateSystemHeight(0, h);
        },
        [updateSystemHeight],
    );
    const onGoalsContentHeight = useCallback(
        (h: number) => {
            updateSystemHeight(3, h);
        },
        [updateSystemHeight],
    );

    const progressYH = useSharedValue(mode === 'system' ? 1 : 0);
    const progressXW = useSharedValue(mode === 'system' ? 1 : 0);
    const progressColor = useSharedValue(mode === 'system' ? 1 : 0);
    const splitTextProgress0 = useSharedValue(0);
    const connectorLineProgress = useSharedValue(0);
    const logoPopProgress = useSharedValue(0);
    const splitTextProgress1 = useSharedValue(0);
    const splitTextProgress2 = useSharedValue(0);
    const splitTextProgress3 = useSharedValue(0);
    const [curvesDrawn, setCurvesDrawn] = useState([false, false, false]);
    const timelineFade = useSharedValue(mode === 'timeline' ? 0 : 1);
    const loopProgressA = useSharedValue(0);
    const loopProgressB = useSharedValue(0);
    const activeLoopIdx = useSharedValue(0);
    const filledGoalCount = useSharedValue(0);
    const activeLoopRef = useRef<boolean>(true);
    const targetGroupA = useSharedValue(0);
    const targetGroupB = useSharedValue(0);
    const [componentWidth, setComponentWidth] = useState(0);
    const [flyingIcons, setFlyingIcons] = useState<Array<{
        source: GoalIconPosition;
        target: GoalIconPosition;
    }> | null>(null);
    const [timelineDrawLines, setTimelineDrawLines] = useState(
        mode === 'timeline' && (drawLinesProp ?? true),
    );
    const flyingDirectionRef = useRef<'toTimeline' | 'toSystem'>('toTimeline');
    const flyingIconTypesRef = useRef<Set<RuleType> | undefined>(undefined);
    const lastSystemGoalIcons = useRef<GoalIconPosition[]>([]);
    const loopTimerRefA = useRef<ReturnType<typeof setTimeout> | null>(null);
    const loopTimerRefB = useRef<ReturnType<typeof setTimeout> | null>(null);
    const incrementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const prevModeRef = useRef(mode);
    if (prevModeRef.current !== mode) {
        if (componentWidth > 0) {
            const allRulesForFlying = (ruleSetDisplays ?? []).flatMap(
                (rs) => rs.rules,
            );
            const timelinePositions = computeTimelineIconTargets(
                allRulesForFlying,
                componentWidth,
                goalsTotalCents,
            );
            const systemPositions =
                lastSystemGoalIcons.current.length > 0
                    ? lastSystemGoalIcons.current
                    : (savedSystemGoalIcons ?? []);

            if (mode === 'timeline' && prevModeRef.current === 'system') {
                setTimelineDrawLines(false);
                const pairs: Array<{
                    source: GoalIconPosition;
                    target: GoalIconPosition;
                }> = [];
                for (const tp of timelinePositions) {
                    const sp = systemPositions.find((s) => s.type === tp.type);
                    if (sp != null) {
                        pairs.push({ source: sp, target: tp });
                    }
                }
                if (pairs.length > 0) {
                    flyingDirectionRef.current = 'toTimeline';
                    flyingIconTypesRef.current = new Set(
                        pairs.map((p) => p.target.type),
                    );
                    setFlyingIcons(pairs);
                }
            } else if (
                mode === 'system' &&
                prevModeRef.current === 'timeline'
            ) {
                setTimelineDrawLines(false);
                const pairs: Array<{
                    source: GoalIconPosition;
                    target: GoalIconPosition;
                }> = [];
                for (const tp of timelinePositions) {
                    const sp = systemPositions.find((s) => s.type === tp.type);
                    if (sp != null) {
                        pairs.push({ source: tp, target: sp });
                    }
                }
                if (pairs.length > 0) {
                    flyingDirectionRef.current = 'toSystem';
                    flyingIconTypesRef.current = new Set(
                        pairs.map((p) => p.target.type),
                    );
                    setFlyingIcons(pairs);
                }
            }
        }
        prevModeRef.current = mode;
    }

    useEffect(() => {
        if (flyingIcons == null) return undefined;
        const flyingDuration = STEP_DURATION + 450 + 100 + 50;
        const timer = setTimeout(() => {
            const direction = flyingDirectionRef.current;
            flyingIconTypesRef.current = undefined;
            setFlyingIcons(null);
            if (direction === 'toTimeline') {
                setTimelineDrawLines(true);
            }
        }, flyingDuration);
        return () => {
            clearTimeout(timer);
        };
    }, [flyingIcons]);

    useEffect(() => {
        if (
            mode === 'timeline' &&
            flyingIcons == null &&
            !timelineDrawLines &&
            (drawLinesProp ?? true)
        ) {
            const timer = setTimeout(() => {
                setTimelineDrawLines(true);
            }, 50);
            return () => {
                clearTimeout(timer);
            };
        }
        return undefined;
    }, [mode, flyingIcons, timelineDrawLines, drawLinesProp]);

    const flushAllPending = useCallback(() => {
        isAnimatingRef.current = false;
        flushPendingHeights();
        flushPendingWidths();
        if (pendingLabelHeightsRef.current.size > 0) {
            const pending = pendingLabelHeightsRef.current;
            setLabelHeights((prev) => {
                let changed = false;
                const next = { ...prev };
                pending.forEach((val, k) => {
                    const existing = next[k];
                    if (
                        existing?.height !== val.height ||
                        existing?.yInRow !== val.yInRow
                    ) {
                        next[k] = val;
                        changed = true;
                    }
                });
                pending.clear();
                return changed ? next : prev;
            });
        }
        if (pendingGoalRowHeightsRef.current.size > 0) {
            const pending = pendingGoalRowHeightsRef.current;
            setGoalRowHeights((prev) => {
                let changed = false;
                const next = { ...prev };
                pending.forEach((h, i) => {
                    if (next[i] !== h) {
                        next[i] = h;
                        changed = true;
                    }
                });
                pending.clear();
                return changed ? next : prev;
            });
        }
    }, [flushPendingHeights, flushPendingWidths]);

    const isInitialRender = useRef(true);
    const prevModeForEffectRef = useRef(mode);

    const timingConfig = {
        duration: STEP_DURATION,
        easing: Easing.inOut(Easing.ease),
    };

    const textTimingConfig = { duration: 500, easing: Easing.out(Easing.ease) };

    useEffect(() => {
        const isFirst = isInitialRender.current;
        const prevMode = prevModeForEffectRef.current;
        isInitialRender.current = false;
        prevModeForEffectRef.current = mode;

        if (disableAnimations) {
            progressYH.value = 1;
            progressXW.value = 1;
            progressColor.value = 1;
            timelineFade.value = mode === 'timeline' ? 0 : 1;
            return undefined;
        }

        if (mode === 'timeline') {
            timelineFade.value = withTiming(0, {
                duration: STEP_DURATION,
                easing: Easing.inOut(Easing.ease),
            });
            loopProgressA.value = 0;
            loopProgressB.value = 0;
            if (loopTimerRefA.current != null) {
                clearTimeout(loopTimerRefA.current);
                loopTimerRefA.current = null;
            }
            if (loopTimerRefB.current != null) {
                clearTimeout(loopTimerRefB.current);
                loopTimerRefB.current = null;
            }
            if (incrementTimerRef.current != null) {
                clearTimeout(incrementTimerRef.current);
                incrementTimerRef.current = null;
            }
            return undefined;
        }

        timelineFade.value = withTiming(1, {
            duration: STEP_DURATION,
            easing: Easing.inOut(Easing.ease),
        });

        if (mode === 'system') {
            const comingFromTimeline = prevMode === 'timeline';

            if (comingFromTimeline) {
                // Timeline → System: bars are already in system position,
                // just restart the income loop immediately
                progressYH.value = 1;
                progressXW.value = 1;
                progressColor.value = 1;
                splitTextProgress0.value = 0;
                connectorLineProgress.value = 0;
                logoPopProgress.value = 0;
                splitTextProgress1.value = 0;
                splitTextProgress2.value = 0;
                splitTextProgress3.value = 0;
                setCurvesDrawn([false, false, false]);

                const loopTimer = setTimeout(() => {
                    startIncomeLoop(1000);
                }, STEP_DURATION);

                return () => {
                    clearTimeout(loopTimer);
                    if (loopTimerRefA.current != null) {
                        clearTimeout(loopTimerRefA.current);
                    }
                    if (loopTimerRefB.current != null) {
                        clearTimeout(loopTimerRefB.current);
                    }
                    if (incrementTimerRef.current != null) {
                        clearTimeout(incrementTimerRef.current);
                    }
                };
            }

            // Split → System: slide/fade text out first, then height/y, then width/x + color
            isAnimatingRef.current = true;
            const textOutDuration = 300;
            splitTextProgress0.value = withTiming(0, {
                duration: textOutDuration,
            });
            connectorLineProgress.value = withTiming(0, {
                duration: textOutDuration,
            });
            logoPopProgress.value = withTiming(0, {
                duration: textOutDuration,
            });
            splitTextProgress1.value = withTiming(0, {
                duration: textOutDuration,
            });
            splitTextProgress2.value = withTiming(0, {
                duration: textOutDuration,
            });
            splitTextProgress3.value = withTiming(0, {
                duration: textOutDuration,
            });
            setCurvesDrawn([false, false, false]);
            progressYH.value = withDelay(
                textOutDuration,
                withTiming(1, timingConfig),
            );
            progressXW.value = withDelay(
                textOutDuration + STEP_DURATION,
                withTiming(1, timingConfig),
            );
            progressColor.value = withDelay(
                textOutDuration + STEP_DURATION,
                withTiming(1, timingConfig),
            );

            // Unified income loop: 0→1 bar slides (500ms), 1→2 dot shoots (dynamic), reset
            const totalTransitionDelay =
                textOutDuration + STEP_DURATION + STEP_DURATION;
            const flushTimer = setTimeout(
                flushAllPending,
                totalTransitionDelay,
            );
            const loopTimer = setTimeout(() => {
                startIncomeLoop(500);
            }, totalTransitionDelay);

            return () => {
                clearTimeout(flushTimer);
                clearTimeout(loopTimer);
                if (loopTimerRefA.current != null) {
                    clearTimeout(loopTimerRefA.current);
                }
                if (loopTimerRefB.current != null) {
                    clearTimeout(loopTimerRefB.current);
                }
                if (incrementTimerRef.current != null) {
                    clearTimeout(incrementTimerRef.current);
                }
            };
        } else {
            // System → Split: width/x + color first, then height/y, then staggered text
            // On initial render, defer the text stagger until systemReady triggers it.
            if (isFirst) {
                loopProgressA.value = 0;
                loopProgressB.value = 0;
                return undefined;
            }

            isAnimatingRef.current = true;
            const baseTextDelay = STEP_DURATION + STEP_DURATION + 100;

            loopProgressA.value = 0;
            loopProgressB.value = 0;

            splitTextProgress0.value = 0;
            connectorLineProgress.value = 0;
            logoPopProgress.value = 0;
            splitTextProgress1.value = 0;
            splitTextProgress2.value = 0;
            splitTextProgress3.value = 0;
            setCurvesDrawn([false, false, false]);
            progressXW.value = withTiming(0, timingConfig);
            progressColor.value = withTiming(0, timingConfig);
            progressYH.value = withDelay(
                STEP_DURATION,
                withTiming(0, timingConfig),
            );

            splitTextProgress0.value = withDelay(
                baseTextDelay,
                withTiming(1, textTimingConfig),
            );
            connectorLineProgress.value = withDelay(
                baseTextDelay + 150,
                withTiming(1, textTimingConfig),
            );
            logoPopProgress.value = withDelay(
                baseTextDelay + 400,
                withTiming(1, textTimingConfig),
            );
            splitTextProgress1.value = withDelay(
                baseTextDelay + 550,
                withTiming(1, textTimingConfig),
            );
            splitTextProgress2.value = withDelay(
                baseTextDelay + 750,
                withTiming(1, textTimingConfig),
            );
            splitTextProgress3.value = withDelay(
                baseTextDelay + 950,
                withTiming(1, textTimingConfig),
            );
            const t1 = setTimeout(() => {
                setCurvesDrawn((prev) => [
                    true,
                    prev[1] ?? false,
                    prev[2] ?? false,
                ]);
            }, baseTextDelay + 550);
            const t2 = setTimeout(() => {
                setCurvesDrawn((prev) => [
                    prev[0] ?? false,
                    true,
                    prev[2] ?? false,
                ]);
            }, baseTextDelay + 750);
            const t3 = setTimeout(() => {
                setCurvesDrawn((prev) => [
                    prev[0] ?? false,
                    prev[1] ?? false,
                    true,
                ]);
            }, baseTextDelay + 950);
            const flushTimer = setTimeout(
                flushAllPending,
                baseTextDelay + 950 + 500,
            );
            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
                clearTimeout(t3);
                clearTimeout(flushTimer);
            };
        }
    }, [mode]);

    const hasStartedEntryAnim = useRef(false);

    const contentWidthRef = useRef(0);
    const onLayout = useCallback((e: LayoutChangeEvent) => {
        const w = e.nativeEvent.layout.width;
        if (w <= 0) return;
        if (
            contentWidthRef.current === 0 ||
            Math.abs(w - contentWidthRef.current) > 2
        ) {
            contentWidthRef.current = w;
            setContentWidth(w);
        }
    }, []);

    const DIM_OPACITY = 0.5;
    const HIGHLIGHT_DURATION = 400;
    const incomeOpacity = useSharedValue(1);
    const billsOpacity = useSharedValue(1);
    const goalsOpacity = useSharedValue(1);

    useEffect(() => {
        const timing = { duration: HIGHLIGHT_DURATION };
        if (highlightedBox == null) {
            incomeOpacity.value = withTiming(1, timing);
            billsOpacity.value = withTiming(1, timing);
            goalsOpacity.value = withTiming(1, timing);
        } else {
            incomeOpacity.value = withTiming(
                highlightedBox === 'income' ? 1 : DIM_OPACITY,
                timing,
            );
            const expensesHighlighted =
                highlightedBox === 'bills' || highlightedBox === 'spend';
            billsOpacity.value = withTiming(
                expensesHighlighted ? 1 : DIM_OPACITY,
                timing,
            );
            goalsOpacity.value = withTiming(
                highlightedBox === 'goals' ? 1 : DIM_OPACITY,
                timing,
            );
        }
    }, [highlightedBox]);

    const incomeHighlightStyle = useAnimatedStyle(() => ({
        opacity: incomeOpacity.value,
    }));
    const billsHighlightStyle = useAnimatedStyle(() => ({
        opacity: billsOpacity.value,
    }));
    const goalsHighlightStyle = useAnimatedStyle(() => ({
        opacity: goalsOpacity.value,
    }));

    const bars = [
        {
            label: 'Bills',
            color: BILLS_COLOR,
            systemColor: BILLS_SYSTEM_COLOR,
            cents: billsTotalCents,
        },
        {
            label: 'Spend',
            color: SPEND_COLOR,
            systemColor: SPEND_SYSTEM_COLOR,
            cents: spendTotalCents,
        },
        {
            label: 'Goals',
            color: GOALS_COLOR,
            systemColor: GOALS_SYSTEM_COLOR,
            cents: goalsTotalCents,
        },
    ];

    const maxBarCents = Math.max(
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
        1,
    );

    const splitOverhead = PILL_HEIGHT + PILL_TO_CURVES_GAP;
    const MIN_CURVES_HEIGHT = 40;

    let effectiveMaxBarHeight: number;
    let effectivePathsHeight: number;

    if (availableHeight != null) {
        const spaceForBarsAndCurves = availableHeight - splitOverhead;
        const defaultTotal = PATHS_HEIGHT + resolvedMaxBarHeight;

        if (spaceForBarsAndCurves >= defaultTotal) {
            effectiveMaxBarHeight = spaceForBarsAndCurves - PATHS_HEIGHT;
            effectivePathsHeight = PATHS_HEIGHT;
        } else if (
            spaceForBarsAndCurves >=
            MIN_CURVES_HEIGHT + resolvedMaxBarHeight
        ) {
            effectiveMaxBarHeight = resolvedMaxBarHeight;
            effectivePathsHeight = spaceForBarsAndCurves - resolvedMaxBarHeight;
        } else {
            effectivePathsHeight = MIN_CURVES_HEIGHT;
            effectiveMaxBarHeight = Math.max(
                MIN_BAR_HEIGHT,
                spaceForBarsAndCurves - MIN_CURVES_HEIGHT,
            );
        }
    } else {
        effectiveMaxBarHeight = resolvedMaxBarHeight;
        effectivePathsHeight = PATHS_HEIGHT;
    }

    const barHeights = bars.map(({ cents }) => {
        const ratio = Math.max(0, cents) / maxBarCents;
        return (
            MIN_BAR_HEIGHT + ratio * (effectiveMaxBarHeight - MIN_BAR_HEIGHT)
        );
    });

    // Available width for bars (after padding)
    const availableWidth = contentWidth > 0 ? contentWidth - 2 : 0;
    const barWidth =
        availableWidth > 0 ? (availableWidth - BAR_GAP * 2) / 3 : 80;

    // Split state: income pill position
    const incomePillWidth = Math.min(availableWidth * 0.5, 160);

    // Split state: bar positions (below the curves)
    const barsTopY = PILL_HEIGHT + PILL_TO_CURVES_GAP;
    const effectiveSvgHeight = effectivePathsHeight + effectiveMaxBarHeight;

    // System state: all boxes stacked vertically at full width, height from content
    const incomeSystemHeight = systemHeights[0] ?? DEFAULT_SYSTEM_HEIGHT;
    const billsSystemHeight = systemHeights[1] ?? DEFAULT_SYSTEM_HEIGHT;
    const goalsSystemHeight = systemHeights[3] ?? DEFAULT_SYSTEM_HEIGHT;

    const systemIncomeLayout: OnboardingMoneyMapBoxLayout = {
        x: 0,
        y: 0,
        width: availableWidth,
        height: incomeSystemHeight,
    };

    const billsSystemY = incomeSystemHeight + SYSTEM_GAP;
    const systemBarLayouts: OnboardingMoneyMapBoxLayout[] = [
        // Bills (i=0): full content height
        {
            x: 0,
            y: billsSystemY,
            width: availableWidth,
            height: billsSystemHeight,
        },
        // Spend (i=1): exactly half Bills height, bottom-anchored to Bills
        {
            x: 0,
            y: billsSystemY + billsSystemHeight / 2,
            width: availableWidth,
            height: billsSystemHeight / 2,
        },
        // Goals bar (i=2): below Bills
        {
            x: 0,
            y: billsSystemY + billsSystemHeight + SYSTEM_GAP,
            width: availableWidth,
            height: goalsSystemHeight,
        },
    ];

    // Container heights — Spend is hidden behind Bills so it doesn't add to total
    const splitContentHeight =
        PILL_HEIGHT + PILL_TO_CURVES_GAP + effectiveSvgHeight;
    const systemTotalHeight =
        incomeSystemHeight +
        SYSTEM_GAP +
        billsSystemHeight +
        SYSTEM_GAP +
        goalsSystemHeight;

    // When containerHeight is provided, center split content vertically
    const splitCenterOffset =
        containerHeightProp != null && containerHeightProp > splitContentHeight
            ? (containerHeightProp - splitContentHeight) / 2
            : 0;
    const splitTotalHeight =
        containerHeightProp != null
            ? Math.max(splitContentHeight, containerHeightProp)
            : splitContentHeight;

    const splitIncomeLayout: OnboardingMoneyMapBoxLayout = {
        x: (availableWidth - incomePillWidth) / 2,
        y: splitCenterOffset,
        width: incomePillWidth,
        height: PILL_HEIGHT,
    };
    const splitBarLayouts: OnboardingMoneyMapBoxLayout[] = bars.map((_, i) => ({
        x: i * (barWidth + BAR_GAP),
        y:
            splitCenterOffset +
            barsTopY +
            effectiveSvgHeight -
            (barHeights[i] ?? MIN_BAR_HEIGHT),
        width: barWidth,
        height: barHeights[i] ?? MIN_BAR_HEIGHT,
    }));

    const spendSystemY = billsSystemY + billsSystemHeight / 2;
    const goalsSystemY = billsSystemY + billsSystemHeight + SYSTEM_GAP;

    useEffect(() => {
        onSystemBoxPositions?.({
            income: { y: 0, height: incomeSystemHeight },
            bills: { y: billsSystemY, height: billsSystemHeight },
            spend: { y: spendSystemY, height: billsSystemHeight / 2 },
            goals: { y: goalsSystemY, height: goalsSystemHeight },
        });
    }, [
        billsSystemY,
        spendSystemY,
        goalsSystemY,
        incomeSystemHeight,
        billsSystemHeight,
        goalsSystemHeight,
        onSystemBoxPositions,
    ]);

    const smoothContainerHeight = useSharedValue(systemTotalHeight);
    useEffect(() => {
        if (smoothContainerHeight.value !== systemTotalHeight) {
            if (disableAnimations) {
                smoothContainerHeight.value = systemTotalHeight;
            } else {
                smoothContainerHeight.value = withTiming(systemTotalHeight, {
                    duration: 150,
                    easing: Easing.out(Easing.ease),
                });
            }
        }
    }, [systemTotalHeight, disableAnimations]);

    // Track vertical positions of label boxes for the connector line
    const [labelHeights, setLabelHeights] = useState<
        Record<string, { height: number; yInRow: number }>
    >({});

    const pendingLabelHeightsRef = useRef<
        Map<string, { height: number; yInRow: number }>
    >(new Map());

    const updateLabelHeight = useCallback(
        (key: string, height: number, yInRow: number) => {
            pendingLabelHeightsRef.current.set(key, { height, yInRow });
            if (isAnimatingRef.current) return;
            setLabelHeights((prev) => {
                const pending = pendingLabelHeightsRef.current;
                if (pending.size === 0) return prev;
                let changed = false;
                const next = { ...prev };
                pending.forEach((val, k) => {
                    const existing = next[k];
                    if (
                        existing?.height !== val.height ||
                        existing?.yInRow !== val.yInRow
                    ) {
                        next[k] = val;
                        changed = true;
                    }
                });
                pending.clear();
                return changed ? next : prev;
            });
        },
        [],
    );

    const [goalRowHeights, setGoalRowHeights] = useState<
        Record<number, number>
    >({});

    const pendingGoalRowHeightsRef = useRef<Map<number, number>>(new Map());

    const updateGoalRowHeight = useCallback((index: number, height: number) => {
        pendingGoalRowHeightsRef.current.set(index, height);
        if (isAnimatingRef.current) return;
        setGoalRowHeights((prev) => {
            const pending = pendingGoalRowHeightsRef.current;
            if (pending.size === 0) return prev;
            let changed = false;
            const next = { ...prev };
            pending.forEach((h, i) => {
                if (next[i] !== h) {
                    next[i] = h;
                    changed = true;
                }
            });
            pending.clear();
            return changed ? next : prev;
        });
    }, []);

    const systemGoalIcons = React.useMemo<GoalIconPosition[]>(() => {
        if (ruleSetDisplays == null || ruleSetDisplays.length === 0) return [];
        const BOX_PAD = 8;
        const ROW_GAP = 8;
        const CARD_PAD = 8;
        const CARD_GAP = 8;
        const positions: GoalIconPosition[] = [];
        let rowY = goalsSystemY + BOX_PAD;
        for (let i = 0; i < ruleSetDisplays.length; i++) {
            const ruleSet = ruleSetDisplays[i];
            if (ruleSet == null) continue;
            const rowH = goalRowHeights[i] ?? 0;
            let cardY = 0;
            for (const rule of ruleSet.rules) {
                positions.push({
                    x: BOX_PAD + maxLabelWidth + 48 + 8 + CARD_PAD + 8,
                    y: rowY + cardY + CARD_PAD + 10,
                    type: rule.type,
                });
                const cardH = 76;
                cardY += cardH + CARD_GAP;
            }
            rowY += rowH + ROW_GAP;
        }
        return positions;
    }, [ruleSetDisplays, goalsSystemY, goalRowHeights, maxLabelWidth]);

    useEffect(() => {
        if (systemGoalIcons.length > 0 && mode !== 'timeline') {
            lastSystemGoalIcons.current = systemGoalIcons;
            onSystemGoalIcons?.(systemGoalIcons);
        }
    }, [systemGoalIcons, onSystemGoalIcons, mode]);

    const labelCenterX = 8 + (maxLabelWidth > 0 ? maxLabelWidth / 2 : 20);

    // Compute line segments between consecutive labels dynamically
    const lineSegments = (() => {
        const goalKeys = Object.keys(labelHeights)
            .filter((k) => k.startsWith('goal-'))
            .sort();

        // Don't render until all measurements are available
        if (
            labelHeights.income == null ||
            labelHeights.bills == null ||
            goalKeys.length === 0 ||
            goalKeys.some(
                (k) => goalRowHeights[Number(k.split('-')[1])] == null,
            ) ||
            systemHeights[0] == null ||
            systemHeights[1] == null ||
            systemHeights[3] == null
        ) {
            return [];
        }

        const incomeLabel = labelHeights.income;
        const billsLabel = labelHeights.bills;
        const goalCount = goalKeys.length;
        const goalsSystemY = (systemBarLayouts[2] ?? { y: 0 }).y;

        // Label positions: boxY + contentPad + label.yInRow gives top of label
        // For income/bills: single XStack row, so yInRow is relative to the XStack
        // which starts at contentPad within the box
        const contentPad = 8;
        const centers: Array<{ y: number; halfH: number }> = [];

        // Income: label y within the content = contentPad + yInRow + height/2
        centers.push({
            y:
                systemIncomeLayout.y +
                contentPad +
                incomeLabel.yInRow +
                incomeLabel.height / 2,
            halfH: incomeLabel.height / 2,
        });
        // Bills: same logic
        const billsBoxY = (systemBarLayouts[0] ?? { y: 0 }).y;
        centers.push({
            y:
                billsBoxY +
                contentPad +
                billsLabel.yInRow +
                billsLabel.height / 2,
            halfH: billsLabel.height / 2,
        });
        // Goal rows: label yInRow is relative to its XStack parent
        // XStack position within YStack = cumulative row heights + gaps
        // YStack position within content = contentPad
        const goalsGap = 8;
        let cumulativeY = goalsSystemY + contentPad;
        for (let g = 0; g < goalCount; g++) {
            const goalLabel = labelHeights[`goal-${g}`];
            if (goalLabel == null) continue;
            const rowH = goalRowHeights[g] ?? 0;
            // Label top = box top + pad + row offset + yInRow
            const labelTop = cumulativeY + goalLabel.yInRow;
            centers.push({
                y: labelTop + goalLabel.height / 2,
                halfH: goalLabel.height / 2,
            });
            cumulativeY += rowH + goalsGap;
        }

        // Build segments between consecutive labels
        const segs: Array<{ top: number; height: number }> = [];
        for (let i = 0; i < centers.length - 1; i++) {
            const current = centers[i];
            const next = centers[i + 1];
            if (current == null || next == null) continue;
            const segTop = current.y + current.halfH;
            const segBottom = next.y - next.halfH;
            if (segBottom > segTop) {
                segs.push({ top: segTop, height: segBottom - segTop });
            }
        }
        return segs;
    })();

    const systemLineStyle = useAnimatedStyle(() => ({
        opacity: interpolate(progressXW.value, [0.5, 1], [0, 1]),
    }));

    const dotSize = 24;
    const segTop = lineSegments.length > 0 ? (lineSegments[0]?.top ?? 0) : 0;
    const segBottom =
        lineSegments.length > 0
            ? (() => {
                  const last = lineSegments[lineSegments.length - 1];
                  return last != null ? last.top + last.height : 0;
              })()
            : 0;
    const lineDotTravel = segBottom - segTop + dotSize * 2;

    // Fraction of travel where the dot passes the bills label (end of first segment)
    const lineDotColorBreak =
        lineDotTravel > 0 && lineSegments.length > 0
            ? ((lineSegments[0]?.height ?? 0) + dotSize / 2) / lineDotTravel
            : 0.5;

    // Fraction of travel occupied by the bills label gap (between segment 0 and segment 1)
    const billsLabelFrac =
        lineDotTravel > 0 && lineSegments.length >= 2
            ? ((lineSegments[1]?.top ?? 0) -
                  ((lineSegments[0]?.top ?? 0) +
                      (lineSegments[0]?.height ?? 0))) /
              lineDotTravel
            : 0;

    // Each fork animation should take a consistent fraction of the phase.
    // Total forks = 1 (bills) + N goals. We give each fork equal real time.
    // The loop endpoint extends past 2.0 so the last fork can fully animate.
    const numGoalGroups = ruleSetDisplays?.length ?? 0;
    const totalForks = numGoalGroups + 1;
    const FORK_ANIM_MS = 1100;
    const baseDuration = totalForks * FORK_ANIM_MS;
    const goalForkRange = FORK_ANIM_MS / baseDuration;
    const barFillRange = 2 * goalForkRange;
    // Hold the dot at the bills level while the monthly expenses fork + bars fill
    const billsHoldDuration = goalForkRange + barFillRange;
    const dotPhaseEnd = 1 + billsHoldDuration + goalForkRange + barFillRange;
    const dotPhaseDuration = baseDuration;

    // Fraction of travel where the dot passes each goal label (shifted by hold)
    const goalForkThresholds: number[] = (() => {
        if (lineDotTravel <= 0 || lineSegments.length < 2) return [];
        const thresholds: number[] = [];
        let cumHeight = 0;
        for (let s = 0; s < lineSegments.length; s++) {
            cumHeight += lineSegments[s]?.height ?? 0;
            if (s >= 1) {
                thresholds.push(
                    (cumHeight + dotSize / 2) / lineDotTravel +
                        billsHoldDuration,
                );
            }
        }
        return thresholds;
    })();

    // Ping-pong dual-SV loop with stagger: when the active loop's dot
    // reaches the goal area, start a NEW loop on the OTHER SV so two dots
    // travel down the connector simultaneously.
    const startIncomeLoop = useCallback(
        (barSlideDuration: number, targetGroup?: number) => {
            const currentGroup = targetGroup ?? filledGoalCount.value;
            const numGroups = goalForkThresholds.length;
            const isLastOrBeyond = currentGroup >= numGroups;
            const isA = activeLoopRef.current;
            const activeSv = isA ? loopProgressA : loopProgressB;
            const activeTimerRef = isA ? loopTimerRefA : loopTimerRefB;

            // Update target group for the SegmentDot
            if (isA) {
                targetGroupA.value = currentGroup;
            } else {
                targetGroupB.value = currentGroup;
            }

            let effectiveDotPhaseEnd: number;
            if (isLastOrBeyond) {
                effectiveDotPhaseEnd = dotPhaseEnd;
            } else {
                const threshold =
                    goalForkThresholds[currentGroup] ?? dotPhaseEnd;
                const buffer = barFillRange * 0.25;
                effectiveDotPhaseEnd = Math.min(
                    dotPhaseEnd,
                    threshold + goalForkRange + barFillRange + buffer,
                );
            }

            const effectiveDotDuration =
                dotPhaseDuration * (effectiveDotPhaseEnd / dotPhaseEnd);
            const effectiveLoopEnd = 1 + effectiveDotPhaseEnd;
            const iterationMs = barSlideDuration + effectiveDotDuration;

            activeSv.value = withSequence(
                withTiming(0, { duration: 0 }),
                withTiming(1, {
                    duration: barSlideDuration,
                    easing: Easing.inOut(Easing.ease),
                }),
                withTiming(effectiveLoopEnd, {
                    duration: effectiveDotDuration,
                    easing: Easing.inOut(Easing.ease),
                }),
            );

            if (activeTimerRef.current != null) {
                clearTimeout(activeTimerRef.current);
            }

            // Stagger: when the dot reaches the goal area, start a new
            // loop on the other SV — even in steady state so two dots
            // are always visible on the connector.
            const staggerGroupIdx = isLastOrBeyond ? 0 : currentGroup;
            const staggerThreshold = goalForkThresholds[staggerGroupIdx];

            if (staggerThreshold != null) {
                const staggerFrac =
                    effectiveDotPhaseEnd > 0
                        ? staggerThreshold / effectiveDotPhaseEnd
                        : 0;
                const staggerDelayMs =
                    barSlideDuration + effectiveDotDuration * staggerFrac;

                const otherTimerRef = isA ? loopTimerRefB : loopTimerRefA;
                if (otherTimerRef.current != null) {
                    clearTimeout(otherTimerRef.current);
                }
                otherTimerRef.current = setTimeout(() => {
                    activeLoopRef.current = !isA;
                    activeLoopIdx.value = isA ? 1 : 0;
                    if (!isLastOrBeyond && currentGroup + 1 < numGroups) {
                        startIncomeLoop(barSlideDuration, currentGroup + 1);
                    } else {
                        startIncomeLoop(barSlideDuration, numGroups);
                    }
                }, staggerDelayMs);

                // Increment filledGoalCount when this loop completes
                // (only while groups are still being filled).
                // Stored on a dedicated ref so the stagger setup
                // on otherTimerRef can't accidentally cancel it.
                if (!isLastOrBeyond) {
                    if (incrementTimerRef.current != null) {
                        clearTimeout(incrementTimerRef.current);
                    }
                    incrementTimerRef.current = setTimeout(() => {
                        filledGoalCount.value = currentGroup + 1;
                    }, iterationMs + 16);
                }
            } else {
                // No goal thresholds — restart sequentially
                activeTimerRef.current = setTimeout(() => {
                    if (!isLastOrBeyond) {
                        filledGoalCount.value = currentGroup + 1;
                    }
                    startIncomeLoop(barSlideDuration);
                }, iterationMs + 16);
            }
        },
        [
            dotPhaseDuration,
            dotPhaseEnd,
            goalForkRange,
            barFillRange,
            goalForkThresholds,
            filledGoalCount,
            loopProgressA,
            loopProgressB,
            activeLoopIdx,
            targetGroupA,
            targetGroupB,
        ],
    );

    const incomeHex = toHex(INCOME_BG);
    const goalsHex = toHex(GOALS_COLOR);

    const systemReadyNow =
        contentWidth > 0 &&
        systemHeights[0] != null &&
        systemHeights[1] != null &&
        systemHeights[3] != null &&
        maxLabelWidth > 0;
    const systemReadyRef = useRef(false);
    if (systemReadyNow) systemReadyRef.current = true;
    const systemReady = systemReadyRef.current;

    const containerOpacity = useSharedValue(0);
    useEffect(() => {
        if (systemReady && containerOpacity.value === 0) {
            containerOpacity.value = 1;
        }
    }, [systemReady]);

    // On initial split render, defer the text stagger until layout is measured
    useEffect(() => {
        if (
            !systemReady ||
            hasStartedEntryAnim.current ||
            disableAnimations ||
            mode !== 'split'
        )
            return;
        hasStartedEntryAnim.current = true;

        const baseTextDelay = 100;
        splitTextProgress0.value = withDelay(
            baseTextDelay,
            withTiming(1, textTimingConfig),
        );
        connectorLineProgress.value = withDelay(
            baseTextDelay + 150,
            withTiming(1, textTimingConfig),
        );
        logoPopProgress.value = withDelay(
            baseTextDelay + 400,
            withTiming(1, textTimingConfig),
        );
        splitTextProgress1.value = withDelay(
            baseTextDelay + 550,
            withTiming(1, textTimingConfig),
        );
        splitTextProgress2.value = withDelay(
            baseTextDelay + 750,
            withTiming(1, textTimingConfig),
        );
        splitTextProgress3.value = withDelay(
            baseTextDelay + 950,
            withTiming(1, textTimingConfig),
        );
        const t1 = setTimeout(() => {
            setCurvesDrawn((prev) => [
                true,
                prev[1] ?? false,
                prev[2] ?? false,
            ]);
        }, baseTextDelay + 550);
        const t2 = setTimeout(() => {
            setCurvesDrawn((prev) => [
                prev[0] ?? false,
                true,
                prev[2] ?? false,
            ]);
        }, baseTextDelay + 750);
        const t3 = setTimeout(() => {
            setCurvesDrawn((prev) => [
                prev[0] ?? false,
                prev[1] ?? false,
                true,
            ]);
        }, baseTextDelay + 950);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, [systemReady, disableAnimations, mode]);

    const containerStyle = useAnimatedStyle(() => ({
        height: interpolate(
            progressYH.value,
            [0, 1],
            [splitTotalHeight, smoothContainerHeight.value],
        ),
        opacity: containerOpacity.value * timelineFade.value,
    }));

    // Fade out curves and labels — tied to the first step (y/height)
    const incomeTopTextStyle = useAnimatedStyle(() => ({
        opacity: splitTextProgress0.value,
        transform: [
            {
                translateY: interpolate(
                    splitTextProgress0.value,
                    [0, 1],
                    [-12, 0],
                ),
            },
        ],
    }));

    const connectorLineStyle = useAnimatedStyle(() => ({
        opacity: connectorLineProgress.value,
        transform: [
            {
                translateY: interpolate(
                    connectorLineProgress.value,
                    [0, 1],
                    [-20, 0],
                ),
            },
        ],
    }));

    const logoPopStyle = useAnimatedStyle(() => ({
        opacity: logoPopProgress.value,
        transform: [
            {
                scale: interpolate(
                    logoPopProgress.value,
                    [0, 0.5, 1],
                    [0, 1.15, 1],
                ),
            },
        ],
    }));

    const incomeBottomTextStyle = useAnimatedStyle(() => ({
        opacity: splitTextProgress0.value,
        transform: [
            {
                translateY: interpolate(
                    splitTextProgress0.value,
                    [0, 1],
                    [12, 0],
                ),
            },
        ],
    }));

    const curveFadeStyle = useAnimatedStyle(() => ({
        opacity: interpolate(progressYH.value, [0, 0.02], [1, 0], 'clamp'),
    }));

    const CURVE_DRAW_DURATION = 3750;

    // SVG curve calculations (same as before)
    const barCenterX = (index: number) => {
        return index * (barWidth + BAR_GAP) + barWidth / 2;
    };

    const startX = availableWidth / 2;
    const startY = STROKE_WIDTH / 2;

    const STRAIGHT_DROP = 4;

    const buildCurve = (endX: number, endY: number) => {
        const dropEnd = startY + Math.min(STRAIGHT_DROP, endY - startY);
        const curveHeight = Math.max(endY - dropEnd, 1);
        const totalHeight = Math.max(endY - startY, 1);
        const ratio = totalHeight / effectiveSvgHeight;
        const c1Multiplier = Math.max(0.05, 0.8 - ratio * (0.8 - 0.1));
        const c2Multiplier = 0.3 - ratio * (0.3 - 0.15);
        const c1y = dropEnd + curveHeight * c1Multiplier;
        const c2y = dropEnd + curveHeight * c2Multiplier;
        return `M ${startX} ${startY} L ${startX} ${dropEnd} C ${startX} ${c1y}, ${endX} ${c2y}, ${endX} ${endY}`;
    };

    const renderOrder = [...bars.entries()].reverse();

    const timelineInStyle = useAnimatedStyle(() => ({
        opacity: interpolate(timelineFade.value, [1, 0], [0, 1], 'clamp'),
    }));

    const handleRootLayout = useCallback(
        (e: LayoutChangeEvent) => {
            setComponentWidth(e.nativeEvent.layout.width);
            onLayout(e);
        },
        [onLayout],
    );

    const isFlying = flyingIcons != null;
    const flyingDir = flyingDirectionRef.current;
    const flyingIconTypes = flyingIconTypesRef.current;
    const showTimeline =
        mode === 'timeline' || (isFlying && flyingDir === 'toSystem');
    const showSystem =
        mode !== 'timeline' || (isFlying && flyingDir === 'toTimeline');
    const timelineIsOverlay =
        isFlying && flyingDir === 'toSystem' && mode !== 'timeline';
    const systemIsOverlay =
        isFlying && flyingDir === 'toTimeline' && mode === 'timeline';
    const SYSTEM_FADE_DURATION = STEP_DURATION;

    return (
        <View
            width="100%"
            {...(graphOnly && showTimeline ? { flex: 1 } : {})}
            onLayout={handleRootLayout}
        >
            {showTimeline && (
                <Animated.View
                    style={[
                        {
                            width: '100%',
                            ...(graphOnly ? { flex: 1 } : {}),
                            ...(timelineIsOverlay
                                ? {
                                      position: 'absolute' as const,
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                  }
                                : {}),
                        },
                        timelineInStyle,
                    ]}
                    pointerEvents={timelineIsOverlay ? 'none' : 'auto'}
                >
                    <TimelineView
                        incomeTotalCents={incomeTotalCents}
                        spendTotalCents={spendTotalCents}
                        goalsTotalCents={goalsTotalCents}
                        ruleSetDisplays={ruleSetDisplays ?? []}
                        onLayout={onLayout}
                        onSpendSlideMove={onSpendSlideMove}
                        onSpendSlideEnd={onSpendSlideEnd}
                        graphOnly={graphOnly}
                        hideIcons={isFlying || (hideIconsProp ?? false)}
                        drawLines={timelineDrawLines}
                        popInIcons={popInIcons}
                        onContentLayout={onTimelineContentLayout}
                    />
                </Animated.View>
            )}

            {flyingIcons?.map((pair) => (
                <FlyingGoalIcon
                    key={`flying-${pair.target.type}-${flyingDir}`}
                    icon={pair.target}
                    sourcePosition={pair.source}
                    moveDelay={SYSTEM_FADE_DURATION / 2}
                    reverse={flyingDir === 'toSystem'}
                />
            ))}

            {showSystem && availableWidth > 0 && (
                <YStack
                    width="100%"
                    gap={16}
                    {...(systemIsOverlay
                        ? { position: 'absolute', top: 0, left: 0, right: 0 }
                        : {})}
                    pointerEvents={systemIsOverlay ? 'none' : 'auto'}
                >
                    <Animated.View
                        style={[
                            {
                                width: '100%',
                                position: 'relative',
                            },
                            containerStyle,
                        ]}
                    >
                        {/* Vertical line connecting label boxes in system state */}
                        {lineSegments.map((seg, idx) => (
                            <Animated.View
                                key={`line-seg-${idx}`}
                                style={[
                                    {
                                        position: 'absolute',
                                        left: labelCenterX - 2,
                                        top: seg.top,
                                        width: 4,
                                        height: Math.max(0, seg.height),
                                        backgroundColor: 'white',
                                        zIndex: 5,
                                    },
                                    systemLineStyle,
                                ]}
                                pointerEvents="none"
                            />
                        ))}

                        {/* Two dots traveling down the connector lines — one per SV */}
                        {lineSegments.map((seg, idx) => (
                            <React.Fragment key={`dot-seg-${idx}`}>
                                <SegmentDot
                                    seg={seg}
                                    segTop={segTop}
                                    lineDotTravel={lineDotTravel}
                                    dotSize={dotSize}
                                    labelCenterX={labelCenterX}
                                    loopProgress={loopProgressA}
                                    progressXW={progressXW}
                                    incomeHex={incomeHex}
                                    goalsHex={goalsHex}
                                    lineDotColorBreak={lineDotColorBreak}
                                    billsHoldDuration={billsHoldDuration}
                                    billsLabelFrac={billsLabelFrac}
                                    targetGroup={targetGroupA}
                                    goalForkThresholds={goalForkThresholds}
                                />
                                <SegmentDot
                                    seg={seg}
                                    segTop={segTop}
                                    lineDotTravel={lineDotTravel}
                                    dotSize={dotSize}
                                    labelCenterX={labelCenterX}
                                    loopProgress={loopProgressB}
                                    progressXW={progressXW}
                                    incomeHex={incomeHex}
                                    goalsHex={goalsHex}
                                    lineDotColorBreak={lineDotColorBreak}
                                    billsHoldDuration={billsHoldDuration}
                                    billsLabelFrac={billsLabelFrac}
                                    targetGroup={targetGroupB}
                                    goalForkThresholds={goalForkThresholds}
                                />
                            </React.Fragment>
                        ))}

                        {/* Income box */}
                        <OnboardingMoneyMapPreviewAnimatedBox
                            progressYH={progressYH}
                            progressXW={progressXW}
                            progressColor={progressColor}
                            splitTextProgress={splitTextProgress0}
                            splitLayout={splitIncomeLayout}
                            systemLayout={systemIncomeLayout}
                            splitColor={toHex(INCOME_BG)}
                            systemColor={toHex(INCOME_SYSTEM_BG)}
                            splitBorderRadius={BAR_RADIUS}
                            zIndex={2}
                            highlightStyle={incomeHighlightStyle}
                            disableAnimations={disableAnimations}
                            onContentHeight={onIncomeContentHeight}
                        >
                            <IncomeSystemContent
                                incomeTotalCents={incomeTotalCents}
                                maxLabelWidth={maxLabelWidth}
                                updateLabelWidth={updateLabelWidth}
                                updateLabelHeight={updateLabelHeight}
                                loopProgressA={loopProgressA}
                                loopProgressB={loopProgressB}
                                activeLoopIdx={activeLoopIdx}
                                disableAnimations={disableAnimations}
                            />
                        </OnboardingMoneyMapPreviewAnimatedBox>

                        {/* Yellow connector line + Fruitful logo box (split state only) */}
                        <Animated.View
                            style={[
                                {
                                    position: 'absolute',
                                    top:
                                        splitCenterOffset +
                                        PILL_HEIGHT +
                                        LOGO_GAP,
                                    left: 0,
                                    right: 0,
                                    alignItems: 'center',
                                    zIndex: 4,
                                },
                            ]}
                            pointerEvents="none"
                        >
                            <Animated.View style={connectorLineStyle}>
                                <View
                                    width={STROKE_WIDTH}
                                    height={CONNECTOR_LINE_HEIGHT}
                                    backgroundColor={INCOME_BG}
                                    borderRadius={STROKE_WIDTH / 2}
                                />
                            </Animated.View>
                            <View height={LOGO_GAP} />
                            <Animated.View style={logoPopStyle}>
                                <View
                                    width={LOGO_BOX_SIZE}
                                    height={LOGO_BOX_SIZE}
                                    backgroundColor="$leafDark"
                                    borderRadius={16}
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Icon
                                        width={20}
                                        height={20}
                                        icon={FruitfulLogoIcon}
                                        color="$white"
                                    />
                                </View>
                            </Animated.View>
                        </Animated.View>

                        {/* Bills + Spend boxes grouped for shared opacity */}
                        <Animated.View
                            style={[
                                {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                },
                                billsHighlightStyle,
                            ]}
                            pointerEvents="box-none"
                        >
                            {bars.slice(0, 2).map((bar, i) => {
                                const fallback: OnboardingMoneyMapBoxLayout = {
                                    x: 0,
                                    y: 0,
                                    width: 0,
                                    height: 0,
                                };
                                const boxZIndex = i === 0 ? 3 : 2;
                                const barTextProgress =
                                    [splitTextProgress1, splitTextProgress2][
                                        i
                                    ] ?? splitTextProgress1;
                                const billsAnchor =
                                    i === 1
                                        ? {
                                              splitLayout:
                                                  splitBarLayouts[0] ??
                                                  fallback,
                                              systemLayout:
                                                  systemBarLayouts[0] ??
                                                  fallback,
                                          }
                                        : undefined;
                                return (
                                    <OnboardingMoneyMapPreviewAnimatedBox
                                        key={bar.label}
                                        progressYH={progressYH}
                                        progressXW={progressXW}
                                        progressColor={progressColor}
                                        splitTextProgress={barTextProgress}
                                        splitLayout={
                                            splitBarLayouts[i] ?? fallback
                                        }
                                        systemLayout={
                                            systemBarLayouts[i] ?? fallback
                                        }
                                        splitColor={toHex(bar.color)}
                                        systemColor={toHex(bar.systemColor)}
                                        splitBorderRadius={BAR_RADIUS}
                                        zIndex={boxZIndex}
                                        systemGradientColors={
                                            i === 0
                                                ? [
                                                      toHex(BILLS_SYSTEM_COLOR),
                                                      toHex(SPEND_SYSTEM_COLOR),
                                                  ]
                                                : undefined
                                        }
                                        anchorBottomTo={billsAnchor}
                                        disableAnimations={disableAnimations}
                                        onContentHeight={(h) => {
                                            updateSystemHeight(i + 1, h);
                                        }}
                                        splitTopContent={
                                            <BodyMediumEmphasized>
                                                {centsToFormattedDollars(
                                                    bar.cents,
                                                    false,
                                                )}
                                            </BodyMediumEmphasized>
                                        }
                                        splitBottomContent={
                                            <BodySmallRegular>
                                                {bar.label}
                                            </BodySmallRegular>
                                        }
                                    >
                                        {i === 0 ? (
                                            <MonthlyExpensesSystemContent
                                                billsTotalCents={
                                                    billsTotalCents
                                                }
                                                spendTotalCents={
                                                    spendTotalCents
                                                }
                                                maxLabelWidth={maxLabelWidth}
                                                updateLabelWidth={
                                                    updateLabelWidth
                                                }
                                                updateLabelHeight={
                                                    updateLabelHeight
                                                }
                                                braceHeight={braceHeight}
                                                setBraceHeight={setBraceHeight}
                                                highlightedSubBox={
                                                    highlightedBox === 'bills'
                                                        ? 'core'
                                                        : highlightedBox ===
                                                            'spend'
                                                          ? 'spend'
                                                          : null
                                                }
                                                loopProgressA={loopProgressA}
                                                loopProgressB={loopProgressB}
                                                activeLoopIdx={activeLoopIdx}
                                                forkTriggerThreshold={
                                                    lineDotColorBreak
                                                }
                                                goalForkRange={goalForkRange}
                                                barFillRange={barFillRange}
                                            />
                                        ) : null}
                                    </OnboardingMoneyMapPreviewAnimatedBox>
                                );
                            })}
                        </Animated.View>

                        {/* Goals box */}
                        {bars[2] != null && (
                            <OnboardingMoneyMapPreviewAnimatedBox
                                key={bars[2].label}
                                progressYH={progressYH}
                                progressXW={progressXW}
                                progressColor={progressColor}
                                splitTextProgress={splitTextProgress3}
                                splitLayout={
                                    splitBarLayouts[2] ?? {
                                        x: 0,
                                        y: 0,
                                        width: 0,
                                        height: 0,
                                    }
                                }
                                systemLayout={
                                    systemBarLayouts[2] ?? {
                                        x: 0,
                                        y: 0,
                                        width: 0,
                                        height: 0,
                                    }
                                }
                                splitColor={toHex(bars[2].color)}
                                systemColor={toHex(bars[2].systemColor)}
                                splitBorderRadius={BAR_RADIUS}
                                zIndex={2}
                                highlightStyle={goalsHighlightStyle}
                                disableAnimations={disableAnimations}
                                onContentHeight={onGoalsContentHeight}
                                splitTopContent={
                                    <BodyMediumEmphasized>
                                        {centsToFormattedDollars(
                                            bars[2].cents,
                                            false,
                                        )}
                                    </BodyMediumEmphasized>
                                }
                                splitBottomContent={
                                    <BodySmallRegular>
                                        {bars[2].label}
                                    </BodySmallRegular>
                                }
                            >
                                <GoalsSystemContent
                                    maxLabelWidth={maxLabelWidth}
                                    updateLabelWidth={updateLabelWidth}
                                    updateLabelHeight={updateLabelHeight}
                                    updateGoalRowHeight={updateGoalRowHeight}
                                    hiddenIconTypes={flyingIconTypes}
                                    ruleSetDisplays={ruleSetDisplays ?? []}
                                    loopProgressA={loopProgressA}
                                    loopProgressB={loopProgressB}
                                    activeLoopIdx={activeLoopIdx}
                                    goalForkThresholds={goalForkThresholds}
                                    goalDotColor={goalsHex}
                                    goalForkRange={goalForkRange}
                                    barFillRange={barFillRange}
                                    filledGoalCount={filledGoalCount}
                                    monthlyGoalsCents={goalsTotalCents}
                                />
                            </OnboardingMoneyMapPreviewAnimatedBox>
                        )}

                        {/* SVG curves - staggered draw from top to bottom */}
                        {renderOrder.map(([index, bar]) => {
                            const endX = barCenterX(index);
                            const endY =
                                effectiveSvgHeight -
                                (barHeights[index] ?? MIN_BAR_HEIGHT) -
                                CURVE_TO_BAR_GAP;
                            return (
                                <Animated.View
                                    key={`${bar.label}-curve`}
                                    style={[
                                        {
                                            position: 'absolute',
                                            top:
                                                splitCenterOffset +
                                                PILL_HEIGHT +
                                                PILL_TO_CURVES_GAP,
                                            left: 0,
                                            right: 0,
                                            height: effectiveSvgHeight,
                                        },
                                        curveFadeStyle,
                                    ]}
                                    pointerEvents="none"
                                >
                                    {availableWidth > 0 && (
                                        <Svg
                                            width="100%"
                                            height={effectiveSvgHeight}
                                            viewBox={`0 0 ${availableWidth} ${effectiveSvgHeight}`}
                                        >
                                            <DrawablePath
                                                d={buildCurve(endX, endY)}
                                                stroke={toHex(bar.color)}
                                                strokeWidth={STROKE_WIDTH}
                                                drawn={
                                                    curvesDrawn[index] ?? false
                                                }
                                                duration={CURVE_DRAW_DURATION}
                                            />
                                        </Svg>
                                    )}
                                </Animated.View>
                            );
                        })}

                        {/* Income text overlay - fade out in system mode */}
                        <Animated.View
                            style={[
                                {
                                    position: 'absolute',
                                    top: splitCenterOffset,
                                    left: 0,
                                    right: 0,
                                    height: PILL_HEIGHT,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 6,
                                },
                            ]}
                            pointerEvents="none"
                        >
                            <Animated.View style={incomeTopTextStyle}>
                                <BodySmallRegular color={INCOME_TEXT}>
                                    Take-home pay
                                </BodySmallRegular>
                            </Animated.View>
                            <Animated.View style={incomeBottomTextStyle}>
                                <BodyMediumEmphasized color={INCOME_TEXT}>
                                    {centsToFormattedDollars(
                                        incomeTotalCents,
                                        false,
                                    )}
                                </BodyMediumEmphasized>
                            </Animated.View>
                        </Animated.View>
                    </Animated.View>
                </YStack>
            )}
        </View>
    );
};
